/**
 * Learning Goals List View
 *
 * Learning goals list with language selector and inline editing.
 * Learning goals are loaded from initial data with all localizations.
 */

import { useState, useMemo, useRef, useEffect } from 'react';
import { DictionariesNavigation } from '../components/DictionariesNavigation';
import { useInitialData, type LearningGoal } from '../../../contexts/InitialDataContext';
import { useUpdateLearningGoalLocalization } from '../services/learningGoalsApi';

/**
 * Get localized name for a learning goal
 * Falls back to English if requested language not available
 */
function getLearningGoalLocalizedName(goal: LearningGoal, languageCode: string): string {
  const localization = goal.localizations.find((loc) => loc.language_code === languageCode);
  if (localization) return localization.localized_name;

  // Fallback to English
  const englishLoc = goal.localizations.find((loc) => loc.language_code === 'en');
  if (englishLoc) return englishLoc.localized_name;

  // Fallback to first available
  if (goal.localizations.length > 0) return goal.localizations[0].localized_name;

  return goal.name;
}

/**
 * Check if learning goal has localization for given language
 */
function hasLocalization(goal: LearningGoal, languageCode: string): boolean {
  return goal.localizations.some((loc) => loc.language_code === languageCode);
}

/**
 * Inline editable cell component
 */
function EditableCell({
  goalId,
  languageCode,
  value,
  canEdit,
  onSave,
  isSaving,
}: {
  goalId: number;
  languageCode: string;
  value: string;
  canEdit: boolean;
  onSave: (goalId: number, languageCode: string, newValue: string) => void;
  isSaving: boolean;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Reset edit value when goal value changes (after save)
  useEffect(() => {
    setEditValue(value);
  }, [value]);

  const handleDoubleClick = () => {
    if (canEdit && !isSaving) {
      setIsEditing(true);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      setEditValue(value);
      setIsEditing(false);
    }
  };

  const handleSave = () => {
    const trimmedValue = editValue.trim();
    if (trimmedValue && trimmedValue !== value) {
      onSave(goalId, languageCode, trimmedValue);
    }
    setIsEditing(false);
  };

  const handleBlur = () => {
    handleSave();
  };

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        className="w-full px-2 py-1 text-sm border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
        data-testid={`edit-input-${goalId}`}
      />
    );
  }

  return (
    <div
      onDoubleClick={handleDoubleClick}
      className={`text-sm font-medium ${
        canEdit
          ? 'text-gray-900 cursor-pointer hover:bg-gray-50 px-2 py-1 -mx-2 -my-1 rounded'
          : 'text-gray-500 italic'
      } ${isSaving ? 'opacity-50' : ''}`}
      title={canEdit ? 'Double-click to edit' : 'No localization for this language'}
      data-testid={`goal-name-${goalId}`}
    >
      {value}
      {!canEdit && ' (fallback)'}
    </div>
  );
}

export function LearningGoalsListView() {
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [displayLanguage, setDisplayLanguage] = useState<string>('en');
  const [savingGoalId, setSavingGoalId] = useState<number | null>(null);

  const { state: initialDataState } = useInitialData();
  const updateLearningGoalLocalization = useUpdateLearningGoalLocalization();

  const languages = initialDataState.data?.languages ?? [];

  // Sort learning goals by display order
  const sortedGoals = useMemo(() => {
    const learningGoals = initialDataState.data?.learningGoals ?? [];
    return [...learningGoals].sort((a, b) => {
      const comparison = a.display_order - b.display_order;
      return sortOrder === 'asc' ? comparison : -comparison;
    });
  }, [initialDataState.data?.learningGoals, sortOrder]);

  const handleSort = () => {
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
  };

  const handleSave = (goalId: number, languageCode: string, newValue: string) => {
    setSavingGoalId(goalId);
    updateLearningGoalLocalization.mutate(
      { learningGoalId: goalId, languageCode, localizedName: newValue },
      {
        onSettled: () => {
          setSavingGoalId(null);
        },
      }
    );
  };

  if (initialDataState.isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (initialDataState.error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-red-600 text-lg mb-4">Error loading learning goals</p>
          <p className="text-gray-600">{initialDataState.error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <DictionariesNavigation />

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">Learning Goals</h1>
          <p className="text-gray-600 mt-1">
            Double-click a goal name to edit. Select a language to view and edit localizations.
          </p>
        </div>
      </div>

      {/* Filters Row */}
      <div className="mb-6 flex gap-4">
        {/* Display Language Selector */}
        <div className="w-48">
          <select
            value={displayLanguage}
            onChange={(e) => setDisplayLanguage(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            data-testid="display-language-selector"
          >
            {languages
              .filter((lang) => lang.code !== 'zxx')
              .map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.name}
                </option>
              ))}
          </select>
        </div>
      </div>

      {/* Learning Goals Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200" data-testid="learning-goals-table">
          <thead className="bg-gray-50">
            <tr>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={handleSort}
                data-testid="sort-order"
              >
                Order {sortOrder === 'asc' ? '↑' : '↓'}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Icon
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Category
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Color
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Available Localizations
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {/* Learning Goal Rows */}
            {sortedGoals.map((goal) => (
              <tr key={goal.id} data-testid={`goal-row-${goal.id}`}>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {goal.display_order}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-2xl">{goal.icon_emoji || '—'}</td>
                <td className="px-6 py-4">
                  <EditableCell
                    goalId={goal.id}
                    languageCode={displayLanguage}
                    value={getLearningGoalLocalizedName(goal, displayLanguage)}
                    canEdit={hasLocalization(goal, displayLanguage)}
                    onSave={handleSave}
                    isSaving={savingGoalId === goal.id}
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                  {goal.category || '—'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {goal.color ? (
                    <div className="flex items-center gap-2">
                      <span
                        className="inline-block w-5 h-5 rounded"
                        style={{ backgroundColor: goal.color }}
                        title={`Filled: ${goal.color}`}
                      />
                      <span
                        className="inline-block w-5 h-5 rounded border border-gray-200"
                        style={{ backgroundColor: goal.color_light }}
                        title={`Light: ${goal.color_light}`}
                      />
                      <span className="text-xs text-gray-500">{goal.color}</span>
                    </div>
                  ) : (
                    <span className="text-sm text-gray-400">—</span>
                  )}
                </td>
                <td className="px-6 py-4">
                  <div className="flex flex-wrap gap-1">
                    {goal.localizations
                      .sort((a, b) => a.language_code.localeCompare(b.language_code))
                      .map((loc) => (
                        <span
                          key={loc.language_code}
                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            loc.language_code === displayLanguage
                              ? 'bg-blue-100 text-blue-800 ring-1 ring-blue-500'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                          title={loc.localized_name}
                        >
                          {loc.language_code.toUpperCase()}
                        </span>
                      ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
